import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
  Fab,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  AttachMoney as MoneyIcon,
  People as PeopleIcon,
  Games as GamesIcon,
  Image as ImageIcon,
  AddPhotoAlternate as AddPhotoIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { packageService, gameService } from '../services/api';
import { Package, Game, PackageGame } from '../types';
import { divertilandiaColors, getCategoryColor, getPriceColor } from '../theme/divertilandiaColors';

const Packages: React.FC = () => {
  const [packages, setPackages] = useState<Package[]>([]);
  const [games, setGames] = useState<Game[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingPackage, setEditingPackage] = useState<Package | null>(null);
  const [validationErrors, setValidationErrors] = useState<{[key: string]: string}>({});
  const [openErrorDialog, setOpenErrorDialog] = useState(false);
  const [errorMessages, setErrorMessages] = useState<string[]>([]);
  const [expandedPackages, setExpandedPackages] = useState<Set<string>>(new Set());
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: 0,
    max_age: 0,
    is_active: true,
    image_base64: '',
    games: [] as Array<{ game_id: string; quantity: number }>,
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [packagesData, gamesData] = await Promise.all([
        packageService.getAll(),
        gameService.getAll()
      ]);
      setPackages(Array.isArray(packagesData) ? packagesData : []);
      setGames(Array.isArray(gamesData) ? gamesData : []);
    } catch (err) {
      setError('Error al cargar los datos');
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (pkg?: Package) => {
    if (pkg) {
      setEditingPackage(pkg);
      setFormData({
        name: pkg.name,
        description: pkg.description || '',
        price: pkg.price,
        max_age: pkg.max_age,
        is_active: pkg.is_active,
        image_base64: pkg.image_base64 || '',
        games: pkg.games?.map(game => ({
          game_id: game.id,
          quantity: game.pivot?.quantity || 1
        })) || [],
      });
    } else {
      setEditingPackage(null);
      setFormData({
        name: '',
        description: '',
        price: 0,
        max_age: 0,
        is_active: true,
        image_base64: '',
        games: [],
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingPackage(null);
    setValidationErrors({});
    setErrorMessages([]);
  };

  const validateForm = () => {
    const errors: {[key: string]: string} = {};
    const errorMessages: string[] = [];

    // Validar nombre
    if (!formData.name.trim()) {
      errors.name = 'El nombre es obligatorio';
      errorMessages.push('• El nombre del paquete es obligatorio');
    }

    // Validar precio
    if (formData.price <= 0) {
      errors.price = 'El precio debe ser mayor a 0';
      errorMessages.push('• El precio debe ser mayor a 0');
    }

    // Validar edad máxima
    if (formData.max_age <= 0) {
      errors.max_age = 'La edad máxima debe ser mayor a 0';
      errorMessages.push('• La edad máxima debe ser mayor a 0');
    }

    // Validar juegos
    if (formData.games.length === 0) {
      errors.games = 'Debe agregar al menos un juego';
      errorMessages.push('• Debe agregar al menos un juego al paquete');
    } else {
      // Validar que todos los juegos tengan un game_id seleccionado
      const invalidGames = formData.games.filter(game => !game.game_id || game.game_id === '');
      if (invalidGames.length > 0) {
        errors.games = 'Todos los juegos deben estar seleccionados';
        errorMessages.push('• Todos los juegos deben estar completamente seleccionados');
      }

      // Validar que todos los juegos tengan cantidad mayor a 0
      const invalidQuantities = formData.games.filter(game => game.quantity <= 0);
      if (invalidQuantities.length > 0) {
        errors.games = 'Todas las cantidades deben ser mayor a 0';
        errorMessages.push('• Todas las cantidades de juegos deben ser mayor a 0');
      }
    }

    return { errors, errorMessages };
  };

  const handleSave = async () => {
    try {
      setError(null);
      setValidationErrors({});

      // Validar el formulario
      const { errors, errorMessages } = validateForm();
      
      if (Object.keys(errors).length > 0) {
        setValidationErrors(errors);
        setErrorMessages(errorMessages);
        setOpenErrorDialog(true);
        return;
      }

      if (editingPackage) {
        await packageService.update(editingPackage.id, formData);
      } else {
        await packageService.create(formData);
      }
      fetchData();
      handleCloseDialog();
    } catch (err: any) {
      console.error('Error saving package:', err);
      
      // Manejar errores del backend
      if (err.response?.data?.errors) {
        const backendErrors = err.response.data.errors;
        const errorMessages: string[] = [];
        const validationErrors: {[key: string]: string} = {};

        Object.keys(backendErrors).forEach(key => {
          const messages = backendErrors[key];
          if (Array.isArray(messages)) {
            errorMessages.push(...messages.map(msg => `• ${msg}`));
            validationErrors[key] = messages[0];
          }
        });

        setValidationErrors(validationErrors);
        setErrorMessages(errorMessages);
        setOpenErrorDialog(true);
      } else {
        setError('Error al guardar el paquete');
      }
    }
  };

  // Función para formatear precios con máximo 2 decimales
  const formatPrice = (price: number | string | null | undefined) => {
    // Convertir a número y validar
    const numPrice = typeof price === 'string' ? parseFloat(price) : Number(price);
    
    // Si no es un número válido, retornar 0
    if (isNaN(numPrice) || numPrice === null || numPrice === undefined) {
      return '0';
    }
    
    return new Intl.NumberFormat('es-MX', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(numPrice);
  };


  const handleDelete = async (id: string) => {
    if (window.confirm('¿Estás seguro de que quieres eliminar este paquete?')) {
      try {
        await packageService.delete(id);
        fetchData();
      } catch (err) {
        setError('Error al eliminar el paquete');
        console.error('Error deleting package:', err);
      }
    }
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const result = e.target?.result as string;
        setFormData(prev => ({
          ...prev,
          image_base64: result,
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const addGame = () => {
    setFormData(prev => ({
      ...prev,
      games: [...prev.games, { game_id: '', quantity: 1 }],
    }));
  };

  const removeGame = (index: number) => {
    setFormData(prev => ({
      ...prev,
      games: prev.games.filter((_, i) => i !== index),
    }));
  };

  const updateGame = (index: number, field: 'game_id' | 'quantity', value: string | number) => {
    setFormData(prev => ({
      ...prev,
      games: prev.games.map((game, i) => 
        i === index ? { ...game, [field]: value } : game
      ),
    }));
  };

  const togglePackageExpansion = (packageId: string) => {
    setExpandedPackages(prev => {
      const newSet = new Set(prev);
      if (newSet.has(packageId)) {
        newSet.delete(packageId);
      } else {
        newSet.add(packageId);
      }
      return newSet;
    });
  };

  const getFilteredPackages = () => {
    const packagesArray = Array.isArray(packages) ? packages : [];
    
    return packagesArray.filter(pkg => {
      const matchesSearch = searchTerm === '' || 
        pkg.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (pkg.description && pkg.description.toLowerCase().includes(searchTerm.toLowerCase()));
      
      return matchesSearch;
    });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Typography 
          variant="h4" 
          sx={{ 
            color: divertilandiaColors.standard.primary,
            fontWeight: 700,
          }}
        >
          Gestión de Paquetes
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
          sx={{ borderRadius: 2 }}
        >
          Nuevo Paquete
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Barra de búsqueda */}
      <Box sx={{ mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={8}>
            <TextField
              fullWidth
              placeholder="Buscar paquetes por nombre o descripción..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <SearchIcon sx={{ color: 'text.secondary', mr: 1 }} />
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: divertilandiaColors.standard.primary,
                  },
                  '&:hover fieldset': {
                    borderColor: divertilandiaColors.standard.primary,
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: divertilandiaColors.standard.primary,
                  },
                },
              }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Box display="flex" justifyContent="flex-end" alignItems="center">
              <Typography variant="body2" color="text.secondary" sx={{ mr: 2 }}>
                Mostrando {getFilteredPackages().length} de {packages.length} paquetes
              </Typography>
              {searchTerm !== '' && (
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setSearchTerm('')}
                >
                  Limpiar
                </Button>
              )}
            </Box>
          </Grid>
        </Grid>
      </Box>

      <Grid container spacing={3}>
        {getFilteredPackages().map((pkg) => (
          <Grid item xs={12} md={6} lg={4} key={pkg.id}>
            <Card sx={{ height: '100%', position: 'relative' }}>
              {/* Imagen del paquete */}
              <Box
                sx={{
                  height: 200,
                  backgroundImage: pkg.image_base64
                    ? `url(${pkg.image_base64})`
                    : divertilandiaColors.gradients.primary,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  position: 'relative',
                  borderRadius: '16px 16px 0 0',
                }}
              >
                {!pkg.image_base64 && (
                  <Typography variant="h1" sx={{ color: 'white', opacity: 0.9, textShadow: '0 2px 4px rgba(0,0,0,0.3)' }}>
                    🎁
                  </Typography>
                )}
                <Box sx={{ position: 'absolute', top: 8, right: 8 }}>
                  <IconButton
                    size="small"
                    onClick={() => handleOpenDialog(pkg)}
                    sx={{ 
                      backgroundColor: 'rgba(255,255,255,0.8)',
                      '&:hover': { backgroundColor: 'rgba(255,255,255,0.9)' }
                    }}
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => handleDelete(pkg.id)}
                    color="error"
                    sx={{ 
                      backgroundColor: 'rgba(255,255,255,0.8)',
                      '&:hover': { backgroundColor: 'rgba(255,255,255,0.9)' }
                    }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              </Box>

              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 2 }}>
                  <Typography variant="h6" component="div" sx={{ fontWeight: 'bold' }}>
                    {pkg.name}
                  </Typography>
                  <Chip
                    label={pkg.is_active ? 'Activo' : 'Inactivo'}
                    size="small"
                    color={pkg.is_active ? 'success' : 'default'}
                  />
                </Box>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {pkg.description}
                </Typography>

                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Box display="flex" alignItems="center" sx={{ mb: 1 }}>
                      <MoneyIcon sx={{ mr: 1, color: getPriceColor(pkg.price) }} />
                      <Typography variant="body2" sx={{ fontWeight: 600, color: getPriceColor(pkg.price) }}>
                        ${formatPrice(pkg.price)}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Box display="flex" alignItems="center" sx={{ mb: 1 }}>
                      <PeopleIcon sx={{ mr: 1, color: divertilandiaColors.rainbow.orange }} />
                      <Typography variant="body2" sx={{ fontWeight: 600, color: divertilandiaColors.rainbow.orange }}>
                        Hasta {pkg.max_age} años
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <Button
                      fullWidth
                      variant="outlined"
                      startIcon={<GamesIcon />}
                      endIcon={expandedPackages.has(pkg.id) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      onClick={() => togglePackageExpansion(pkg.id)}
                      sx={{ 
                        justifyContent: 'space-between',
                        textTransform: 'none',
                        borderColor: divertilandiaColors.standard.info,
                        color: divertilandiaColors.standard.info,
                        borderWidth: 2,
                        '&:hover': {
                          backgroundColor: divertilandiaColors.backgrounds.light.indigo,
                          borderColor: divertilandiaColors.standard.info,
                          borderWidth: 2,
                        }
                      }}
                    >
                      <Box display="flex" alignItems="center">
                        <Typography variant="body2">
                          {pkg.games?.length || 0} juegos incluidos
                        </Typography>
                      </Box>
                    </Button>
                  </Grid>
                </Grid>

                {/* Lista expandible de juegos */}
                {pkg.games && pkg.games.length > 0 && expandedPackages.has(pkg.id) && (
                  <Box sx={{ mt: 2 }}>
                    <Divider sx={{ mb: 2 }} />
                    <Typography variant="subtitle2" sx={{ mb: 2, color: divertilandiaColors.standard.info, fontWeight: 'bold' }}>
                      Juegos incluidos:
                    </Typography>
                    <List>
                      {pkg.games.map((game, index) => (
                        <ListItem key={index} sx={{ px: 0, py: 1 }}>
                          <Box display="flex" alignItems="center" width="100%">
                            {/* Imagen del juego */}
                            <Box
                              sx={{
                                width: 50,
                                height: 50,
                                borderRadius: 1,
                                backgroundImage: game.image_base64
                                  ? `url(${game.image_base64})`
                                  : `linear-gradient(135deg, ${getCategoryColor(game.toy_type?.name || 'otros')}30, ${getCategoryColor(game.toy_type?.name || 'otros')}60)`,
                                backgroundSize: 'cover',
                                backgroundPosition: 'center',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                mr: 2,
                                flexShrink: 0,
                                border: `3px solid ${getCategoryColor(game.toy_type?.name || 'otros')}`,
                                boxShadow: `0 2px 8px ${getCategoryColor(game.toy_type?.name || 'otros')}30`,
                              }}
                            >
                              {!game.image_base64 && (
                                <Typography variant="h6" sx={{ 
                                  color: 'white',
                                  textShadow: '0 1px 2px rgba(0,0,0,0.5)',
                                  fontWeight: 'bold'
                                }}>
                                  🎯
                                </Typography>
                              )}
                            </Box>
                            
                            {/* Información del juego */}
                            <Box flex={1}>
                              <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                {game.name}
                              </Typography>
                              <Typography variant="caption" sx={{ 
                                color: getCategoryColor(game.toy_type?.name || 'otros'), 
                                fontWeight: 700,
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px'
                              }}>
                                {game.toy_type?.name}
                              </Typography>
                              <Box display="flex" alignItems="center" sx={{ mt: 0.5 }}>
                                <Chip
                                  label={`x${game.pivot?.quantity || 1}`}
                                  size="small"
                                  sx={{ 
                                    mr: 1,
                                    backgroundColor: getCategoryColor(game.toy_type?.name || 'otros'),
                                    color: 'white',
                                    fontWeight: 700,
                                    border: `1px solid ${getCategoryColor(game.toy_type?.name || 'otros')}80`,
                                    boxShadow: `0 2px 4px ${getCategoryColor(game.toy_type?.name || 'otros')}30`,
                                  }}
                                />
                                <Typography variant="caption" sx={{ 
                                  color: divertilandiaColors.standard.accent, 
                                  fontWeight: 700,
                                  fontSize: '0.75rem'
                                }}>
                                  ${formatPrice(game.unit_price || 0)} c/u
                                </Typography>
                              </Box>
                            </Box>
                          </Box>
                        </ListItem>
                      ))}
                    </List>
                    
                    {/* Leyenda de colores por categoría */}
                    <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                      <Typography variant="caption" sx={{ 
                        color: 'text.secondary', 
                        fontWeight: 600, 
                        mb: 1, 
                        display: 'block' 
                      }}>
                        Leyenda de colores:
                      </Typography>
                      <Box display="flex" flexWrap="wrap" gap={1}>
                        {Array.from(new Set(pkg.games?.map(game => game.toy_type?.name).filter(Boolean))).map((categoryName, index) => (
                          <Box key={index} display="flex" alignItems="center" sx={{ mr: 2, mb: 0.5 }}>
                            <Box
                              sx={{
                                width: 12,
                                height: 12,
                                borderRadius: '50%',
                                backgroundColor: getCategoryColor(categoryName || 'otros'),
                                mr: 1,
                                border: '1px solid rgba(0,0,0,0.1)',
                              }}
                            />
                            <Typography variant="caption" sx={{ 
                              color: 'text.secondary',
                              fontSize: '0.7rem',
                              fontWeight: 500
                            }}>
                              {categoryName}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Mensaje cuando no hay resultados de búsqueda */}
      {getFilteredPackages().length === 0 && packages.length > 0 && (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <SearchIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No se encontraron paquetes
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            No hay paquetes que coincidan con "{searchTerm}"
          </Typography>
          <Button
            variant="outlined"
            onClick={() => setSearchTerm('')}
          >
            Limpiar búsqueda
          </Button>
        </Box>
      )}

      {/* Mensaje cuando no hay paquetes en absoluto */}
      {packages.length === 0 && !loading && (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <GamesIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No hay paquetes registrados
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Comienza agregando tu primer paquete
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            Agregar Primer Paquete
          </Button>
        </Box>
      )}

      {/* Dialog para crear/editar paquete */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="lg" fullWidth>
        <DialogTitle>
          {editingPackage ? 'Editar Paquete' : 'Nuevo Paquete'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {/* Información básica */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ color: 'primary.main' }}>
                Información Básica
              </Typography>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Nombre del Paquete"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                sx={{ mt: 2, mb: 1 }}
                required
                error={!!validationErrors.name}
                helperText={validationErrors.name}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Edad Máxima (años)"
                type="number"
                value={formData.max_age}
                onChange={(e) => handleInputChange('max_age', parseInt(e.target.value))}
                sx={{ mt: 2, mb: 1 }}
                required
                error={!!validationErrors.max_age}
                helperText={validationErrors.max_age}
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Descripción"
                multiline
                rows={3}
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                sx={{ mt: 2, mb: 1 }}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Precio"
                type="number"
                value={formData.price}
                onChange={(e) => handleInputChange('price', parseFloat(e.target.value))}
                sx={{ mt: 2, mb: 1 }}
                InputProps={{
                  startAdornment: <Typography sx={{ mr: 1 }}>$</Typography>,
                }}
                required
                error={!!validationErrors.price}
                helperText={validationErrors.price}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_active}
                    onChange={(e) => handleInputChange('is_active', e.target.checked)}
                  />
                }
                label="Paquete Activo"
                sx={{ mt: 3 }}
              />
            </Grid>

            {/* Imagen */}
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" gutterBottom sx={{ color: 'primary.main' }}>
                Imagen del Paquete
              </Typography>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  height: 200,
                  backgroundImage: formData.image_base64
                    ? `url(${formData.image_base64})`
                    : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '2px dashed #ccc',
                  borderRadius: 1,
                  position: 'relative',
                }}
              >
                {!formData.image_base64 && (
                  <Box textAlign="center">
                    <ImageIcon sx={{ fontSize: 48, color: 'white', opacity: 0.7, mb: 1 }} />
                    <Typography variant="body2" sx={{ color: 'white', opacity: 0.7 }}>
                      Sin imagen
                    </Typography>
                  </Box>
                )}
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    opacity: 0,
                    cursor: 'pointer',
                  }}
                />
              </Box>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Button
                variant="outlined"
                startIcon={<AddPhotoIcon />}
                onClick={() => (document.querySelector('input[type="file"]') as HTMLInputElement)?.click()}
                sx={{ mt: 2 }}
              >
                Seleccionar Imagen
              </Button>
            </Grid>

            {/* Juegos */}
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Box>
                  <Typography variant="h6" sx={{ color: validationErrors.games ? 'error.main' : 'primary.main' }}>
                    Juegos Incluidos *
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Agrega al menos un juego al paquete
                  </Typography>
                  {validationErrors.games && (
                    <Typography variant="body2" color="error" sx={{ mt: 0.5 }}>
                      {validationErrors.games}
                    </Typography>
                  )}
                </Box>
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={addGame}
                  size="small"
                >
                  Agregar Juego
                </Button>
              </Box>
            </Grid>
            
            {formData.games.length === 0 && (
              <Grid item xs={12}>
                <Alert severity="info" sx={{ mb: 2 }}>
                  No hay juegos agregados. Haz clic en "Agregar Juego" para comenzar.
                </Alert>
              </Grid>
            )}
            
            {formData.games.map((game, index) => (
              <Grid item xs={12} key={index}>
                <Card variant="outlined" sx={{ p: 2 }}>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={5}>
                      <FormControl fullWidth>
                        <InputLabel>Seleccionar Juego *</InputLabel>
                        <Select
                          value={game.game_id}
                          onChange={(e) => updateGame(index, 'game_id', e.target.value)}
                          label="Seleccionar Juego *"
                          error={!game.game_id}
                        >
                          <MenuItem value="">
                            <em>-- Selecciona un juego --</em>
                          </MenuItem>
                          {games.map((g) => (
                            <MenuItem key={g.id} value={g.id}>
                              {g.name} - {g.toy_type?.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <TextField
                        fullWidth
                        label="Cantidad"
                        type="number"
                        value={game.quantity}
                        onChange={(e) => updateGame(index, 'quantity', parseInt(e.target.value))}
                        inputProps={{ min: 1 }}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Box display="flex" justifyContent="flex-end">
                        <IconButton
                          onClick={() => removeGame(index)}
                          color="error"
                          size="small"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    </Grid>
                  </Grid>
                </Card>
              </Grid>
            ))}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancelar</Button>
          <Button onClick={handleSave} variant="contained">
            {editingPackage ? 'Actualizar' : 'Crear'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de Errores de Validación */}
      <Dialog open={openErrorDialog} onClose={() => setOpenErrorDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ color: 'error.main', display: 'flex', alignItems: 'center' }}>
          <Alert severity="error" sx={{ mr: 2, p: 0, backgroundColor: 'transparent' }}>
            ⚠️
          </Alert>
          Error de Validación
        </DialogTitle>
        <DialogContent>
          <Typography variant="h6" gutterBottom>
            No se pudo guardar el paquete. Por favor corrige los siguientes errores:
          </Typography>
          <Box sx={{ mt: 2 }}>
            {errorMessages.map((message, index) => (
              <Typography key={index} variant="body1" sx={{ mb: 1, color: 'error.main' }}>
                {message}
              </Typography>
            ))}
          </Box>
          <Alert severity="info" sx={{ mt: 2 }}>
            Los campos marcados en rojo son obligatorios o tienen valores inválidos.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenErrorDialog(false)} variant="contained">
            Entendido
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Packages;
